import { Button, Form, Input, Modal, Select, Spin, Typography } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Socket } from "socket.io-client";

import { useGetConfigurationFee, useGetExchangeRate } from "../../../api/send-money";
import { useExchangeData, useGetAvlBalanceInternal } from "../../../api/withdrawal";
import { ReactComponent as CheckIcon } from '../../../common/images/svg/Check.svg';
import { ReactComponent as CloseIcon } from "../../../common/images/svg/CloseIcon.svg";
import { ReactComponent as ArrowIcon } from "../../../common/images/svg/SuffixSelectIcon.svg";
import smartLogoDefault from '../../../common/images/svg/smart-logo-icon.svg';
import { CustomButton } from "../../../components/button/buttonCustom";
import ConfirmModal from "../../../components/modal/confirmModal";
import { useNotification } from "../../../components/providers/notification/useNotification";
import { useAllMetadataCurrency } from "../../../utils/hooks/useAllMetadataCurrency";
import useResponsive from "../../../utils/hooks/useResponsive";
import { formatNumber, roundTo, roundUp } from "../../../utils/list.utils";

const { Option, OptGroup } = Select

interface DataCryptoProps {
  id: number;
  symbol: string;
  name: string;
  iconPath: string;
  totalBalance: number;
  decimals: number;
  amount: number;
  avlBalance: number;
}

interface ModalExchangeProps {
  selectedItem: any;
  setSelectedItem: React.Dispatch<any>;
  socket: Socket<any, any>;
  dataListCrypto: DataCryptoProps[];
}

const Direction = {
  PAY_TO_RCV: 'PAY_TO_RCV',
  RCV_TO_PAY: 'RCV_TO_PAY',
}

const ModalExchange = ({
  selectedItem,
  setSelectedItem,
  socket,
}: ModalExchangeProps) => {
  const { isTablet } = useResponsive();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [fromCurrency, setFromCurrency] = useState<any>();
  const [toCurrency, setToCurrency] = useState<any>();
  const [rate, setRate] = useState<number>(0);
  const [fromDecimal, setFromDecimal] = useState<number>(0);
  const [toDecimal, setToDecimal] = useState<number>(0);
  const [fromAmountError, setFromAmountError] = useState<boolean>(false);
  const [toAmountError, setToAmountError] = useState<boolean>(false);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [configFee, setConfigFee] = useState<any>(null);
  const [configFeeToCurrency, setConfigFeeToCurrency] = useState<any>(null);
  const [loadingFee, setLoadingFee] = useState<boolean>(false);
  const [loadingFeeToCurrency, setLoadingFeeToCurrency] = useState<boolean>(false);
  const [totalBalance, setTotalBalance] = useState<any>(null);
  const [decimals, setDecimals] = useState<number | null>(null);
  const [toAmountMinError, setToAmountMinError] = useState<string>("");
  const [direction, setDirection] = useState<'PAY_TO_RCV' | 'RCV_TO_PAY' | string>(Direction.PAY_TO_RCV);
  const [lastChange, setLastChange] = useState<'FROM' | 'TO'>('FROM');
  const watchedFromAmount = Form.useWatch('fromAmount', form);
  const watchedToAmount = Form.useWatch('toAmount', form);
  const [showReviewFee, setShowReviewFee] = useState(false)

  // Refs to track currency changes and preserve errors
  const isChangingCurrencyRef = useRef(false);
  const hasUserEditedInputRef = useRef(false);
  const preservedToAmountErrorRef = useRef<string | null>(null);
  const preservedFromAmountErrorRef = useRef<string | null>(null);
  const isFromAmountFocusedRef = useRef(false);
  const isToAmountFocusedRef = useRef(false);

  const { dataAllCurrency } = useAllMetadataCurrency()
  const currencyTokens = dataAllCurrency as any[];
  const { open: openNotification } = useNotification();

  const { data: rateData, isLoading: loadingRate, refetch: refetchRate } =
    useGetExchangeRate({ from: fromCurrency?.id, to: toCurrency?.id } as any);

  const {
    data: avlBalance,
    isFetching: loadingAvlBalance,
    refetch,
  } = useGetAvlBalanceInternal(Number(fromCurrency?.id));

  const { mutate: fetchConfigurationFee, isLoading: loadingFeeConfig } =
    useGetConfigurationFee();

  const { mutateAsync: exchangeData, isLoading: loadingExchangeData } =
    useExchangeData({
      onSuccess: (response: {
        data: {
          data: any;
          message: string;
        };
      }) => {
        if (openNotification) {
          openNotification({
            key: "create_success",
            message: "Exchange order submitted successfully!",
            type: "success",
          });
        }
        setSelectedItem(null);
        form.setFields([
          { name: 'fromAmount', errors: [] },
          { name: 'toAmount', errors: [] }
        ]);
        setFromAmountError(false);
        setToAmountError(false);
        setToAmountMinError("");
        form.setFieldValue('fromAmount', null);
        form.setFieldValue('toAmount', null);
        setFromCurrency(selectedItem);
        const isCrypto = fromCurrency?.type === 'CRYPTO';
        navigate(`/history?tab=${isCrypto ? 'crypto' : 'fiat'}&orderId=${response?.data?.data?.id}&type=exchange`);
      },
      onError: (error: { message: string; reason_code: string }) => {
        if (error?.reason_code === 'ERR00103') {
          setFromAmountError(true);
          form.setFields([
            { name: 'fromAmount', errors: [error?.message] },
          ]);
        } else if (error?.reason_code === 'ERR00104') {
          setShowReviewFee(true)
        } else if (openNotification) {
          openNotification({
            key: 'fee_error',
            message: error?.message,
            type: 'error',
          });
        }
      },
    });

  const roundDown = useCallback((value: number, decimals: number) => {
    const factor = Math.pow(10, decimals);
    return Math.floor(value * factor) / factor;
  }, []);

  /**
   * Round down a value with minimum precision guarantee.
   * If the result is 0.00, increases decimal places until a non-zero value is found.
   * @param value - The value to round down
   * @param decimals - Initial decimal places (default: 2)
   * @param maxDecimals - Maximum decimal places to try (default: 18)
   * @returns The rounded down value with appropriate precision
   */
  const roundDownWithMinimum = useCallback((
    value: number,
    decimals = 2,
    maxDecimals = 18
  ): { value: number; decimals: number } => {
    let currentDecimals = decimals;
    let result = roundDown(value, currentDecimals);

    // If result is 0 and value is not 0, increase decimal places
    while (result === 0 && value !== 0 && currentDecimals < maxDecimals) {
      currentDecimals += 2; // Increase by 2 decimal places each time
      result = roundDown(value, currentDecimals);
    }

    return { value: result, decimals: currentDecimals };
  }, [roundDown]);

  const exchangeRateText = useMemo(() => {
    if (!fromCurrency || !toCurrency || !rate) return "";
    const fromCurrencySymbol = fromCurrency?.symbol || ''
    const toCurrencySymbol = toCurrency?.symbol || ''
    const toCurrencyNumber = Number(rate) && toDecimal ? 1 * Number(rate) : 0;
    const { value: roundedValue, decimals: finalDecimals } = roundDownWithMinimum(toCurrencyNumber, toDecimal);

    return `Indicated rate (1 minute): 1 ${fromCurrencySymbol} ≈ ${formatNumber(
      roundedValue,
      finalDecimals,
    )} ${toCurrencySymbol}`;
  }, [fromCurrency, toCurrency, rate, toDecimal]);

  const fetchFee = (currencyId: number | undefined) => {
    if (!currencyId) {
      console.log('fetchFee: currencyId is invalid', currencyId);
      setLoadingFee(false);
      return;
    }
    setLoadingFee(true);
    fetchConfigurationFee(currencyId, {
      onSuccess: (res: { data: { data: any } }) => {
        setLoadingFee(false);
        setLoadingFeeToCurrency(false);
        if (res?.data?.data) {
          setConfigFee(res.data.data);
        }
      },
      onError: (error: any) => {
        setLoadingFee(false);
        setLoadingFeeToCurrency(false);
      },
    })
  };

  const fetchFeeToCurrency = (currencyId: number | undefined) => {
    if (!currencyId) {
      setLoadingFeeToCurrency(false);
      return;
    }
    setLoadingFeeToCurrency(true);
    fetchConfigurationFee(currencyId, {
      onSuccess: (res: { data: { data: any } }) => {
        setLoadingFee(false);
        setLoadingFeeToCurrency(false);
        if (res?.data?.data) {
          setConfigFeeToCurrency(res.data.data);
        }
      },
      onError: (error: any) => {
        setLoadingFee(false);
        setLoadingFeeToCurrency(false);
      },
    });
  };

  // Filter currencies to only ETH, DAI, USDT
  const filteredCurrencyTokens = useMemo(() => {
    if (!currencyTokens) return [];
    const allowedSymbols = ['ETH', 'DAI', 'USDT', 'USD', 'EUR', 'HKD', 'AUD', 'JPY', 'CHF', 'CNY', 'AED', 'SGD', 'GBP'];
    return currencyTokens.filter((token: any) =>
      allowedSymbols.includes(token.symbol?.toUpperCase())
    );
  }, [currencyTokens]);

  // Calculate conversion fee based on configFee
  const calculateConversionFee = useMemo(() => {
    if (!configFee || !fromCurrency || !fromDecimal) return 0;
    const fromAmountValue = form.getFieldValue('fromAmount');
    const feeType = configFee?.exchangeMoneyFeeType;
    switch (feeType) {
      case 'PERCENTAGE': {
        if (!fromAmountValue) return 0;
        return Number(
          roundUp(
            (parseFloat(fromAmountValue) * configFee?.exchangeMoneyFee) / 100,
            fromDecimal
          )
        );
      }
      case 'FIXED':
        return Number(
          roundUp(
            configFee?.exchangeMoneyFee * configFee?.usdToFeeCurrencyRate,
            fromDecimal
          )
        );
      default:
        return 0; // Default fee
    }
  }, [configFee, toCurrency, toDecimal, form, fromCurrency, fromDecimal, watchedToAmount]);

  const calculateConversionFeeToCurrency = (value?: number) => {
    if (!configFee || !fromCurrency || !fromDecimal) return 0;
    const fromAmountValue = value || form.getFieldValue('fromAmount');

    const feeType = configFee?.exchangeMoneyFeeType;
    switch (feeType) {
      case 'PERCENTAGE': {
        if (!fromAmountValue) return 0;
        return Number(
          roundUp(
            (parseFloat(fromAmountValue) * configFee?.exchangeMoneyFee) / 100,
            fromDecimal
          )
        );
      }
      case 'FIXED':
        return Number(
          roundUp(
            configFee?.exchangeMoneyFee * configFee?.usdToFeeCurrencyRate,
            fromDecimal
          )
        );
      default:
        return 0; // Default fee
    }
  };

  const handleCancelModal = () => {
    setSelectedItem(null);
    form.setFields([
      { name: 'fromAmount', errors: [] },
      { name: 'toAmount', errors: [] }
    ]);
    setToAmountError(false);
    setFromAmountError(false);
    setToAmountMinError("");
    form.setFieldValue('fromAmount', null);
    form.setFieldValue('toAmount', null);
    setFromCurrency(selectedItem);
    setToAmountMinError("");
    navigate("/home");
  };

  const getMaxLimit = (currency: any, isToCurrency: boolean | undefined = false) => {
    if (!currency) return null;

    const liveAvlBalance = avlBalance?.data?.data?.totalBalance;

    // For fromCurrency: use live available balance or selectedItem balance
    if (!isToCurrency) {
      // Prefer live available balance from API for current from currency
      if (
        liveAvlBalance !== undefined &&
        liveAvlBalance !== null &&
        currency.id === Number(fromCurrency?.id)
      ) {
        const totalBalanceData = liveAvlBalance;
        return totalBalanceData;
      }
      // Use totalBalance state if available and currency matches
      if (
        totalBalance !== null &&
        totalBalance !== undefined &&
        currency.id === Number(fromCurrency?.id)
      ) {
        const totalBalanceData = totalBalance;
        return totalBalanceData;
      }
      // Use selectedItem balance if it matches
      if (selectedItem && currency.id === selectedItem.id) {
        const totalBalanceData = selectedItem.totalBalance;
        return totalBalanceData;
      }
      // Fallback: return null if no balance found
      return null;
    }

    // For toCurrency: max is totalBalance of fromCurrency converted to toCurrency
    if (isToCurrency) {
      // Get totalBalance of fromCurrency
      // Prefer live available balance, then totalBalance state, then selectedItem balance
      let convertedData = 0;
      if (liveAvlBalance !== undefined) {
        const conversionFee = calculateConversionFeeToCurrency(liveAvlBalance);
        const val = Number(liveAvlBalance - conversionFee);
        const converted = Number(((val > 0 ? val : 0) * rate)).toFixed(toDecimal);
        convertedData = parseFloat(converted) || 0;
      } else if (totalBalance !== null && totalBalance !== undefined) {
        const conversionFee = calculateConversionFeeToCurrency(totalBalance);
        const val = Number(totalBalance - conversionFee);
        const converted = Number(((val > 0 ? val : 0) * rate)).toFixed(toDecimal);
        convertedData = parseFloat(converted) || 0;
      } else if (selectedItem && fromCurrency?.id === selectedItem.id) {
        const conversionFee = calculateConversionFeeToCurrency(selectedItem.totalBalance);
        const val = Number(selectedItem.totalBalance - conversionFee);
        const converted = Number(((val > 0 ? val : 0) * rate)).toFixed(toDecimal);
        convertedData = parseFloat(converted) || 0;
      }
      // Convert fromCurrency balance to toCurrency using rate
      if (rate && rate > 0 && convertedData > 0) {
        return convertedData || 0;
      }

      return 0;
    }

    return 0;
  };

  const validateAndCalculateFromAmount = (rawValue: any) => {
    const val = Number(rawValue);

    // Set direction to PAY_TO_RCV when inputting fromAmount
    setDirection(Direction.PAY_TO_RCV);

    // Reset when empty or invalid
    if (!rawValue || isNaN(val) || val <= 0) {
      setFromAmountError(false);
      form.setFields([{ name: 'fromAmount', errors: [] }]);
      form.setFieldValue('toAmount', undefined);
      return;
    }

    // Validate min amount first if configFee are available
    if (configFee && !loadingFee && !loadingFeeToCurrency) {
      const minFrom = getMinFromAmount();
      if (minFrom !== null && val < minFrom) {
        setFromAmountError(true);
        form.setFields([
          {
            name: 'fromAmount',
            errors: [
              `The amount you pay must be at least ${formatNumber(minFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`,
            ],
          },
        ]);
        // Clear toAmount errors when fromAmount is invalid
        setToAmountError(false);
        setToAmountMinError("");
        form.setFields([{ name: 'toAmount', errors: [] }]);
        return;
      }
    }

    // Then validate max limit
    const maxLimit = getMaxLimit(fromCurrency);
    if (maxLimit !== null && maxLimit > 0 && val > maxLimit) {
      setFromAmountError(true);
      form.setFields([
        {
          name: 'fromAmount',
          errors: [
            `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimit, fromDecimal)} ${fromCurrency?.symbol}`
          ],
        },
      ]);
      // Clear toAmount errors when fromAmount is invalid
      setToAmountError(false);
      setToAmountMinError("");
      form.setFields([{ name: 'toAmount', errors: [] }]);
      return;
    }

    // Only calculate and validate toAmount after fromAmount is valid
    // Calculate toAmount based on fromAmount: toAmount = fromAmount * rate (PAY_TO_RCV direction)
    if (fromCurrency && toCurrency && rate > 0) {
      setIsCalculating(true);
      const conversionFee = calculateConversionFeeToCurrency(val);
      const val2 = Number(val - conversionFee);
      const converted = Number((val2 > 0 ? val2 : 0) * rate).toFixed(toDecimal);
      const convertedNum = Number(converted);
      form.setFieldValue('toAmount', convertedNum >= 0 ? converted : null);

      // Validate toAmount after calculation - only if fromAmount is valid
      // Validate min amount first if configFee is available
      if (configFee && !loadingFee) {
        const minTo = getMinToAmount();
        if (minTo !== null && convertedNum < minTo) {
          const errorMsg = `The amount you receive must be at least ${formatNumber(minTo, toDecimal)} ${toCurrency?.symbol || ''}`;
          setToAmountMinError(errorMsg);
          setToAmountError(false);
          form.setFields([{ name: 'toAmount', errors: [errorMsg] }]);
        } else {
          // Then validate max limit for toAmount
          const maxLimitTo = getMaxLimit(toCurrency, true);
          if (maxLimitTo !== null && convertedNum > maxLimitTo) {
            // Calculate backwards from toAmount to fromAmount
            const fromAmountForMaxTo = (maxLimitTo / rate) + conversionFee;

            // Prioritize checking max limit of fromAmount first
            const maxLimitFrom = getMaxLimit(fromCurrency);
            if (maxLimitFrom !== null && fromAmountForMaxTo > maxLimitFrom) {
              // fromAmount for max toAmount exceeds fromCurrency max limit
              // Show fromCurrency max limit error instead
              const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol}`;
              setFromAmountError(true);
              setToAmountError(false);
              setToAmountMinError("");
              form.setFields([
                {
                  name: 'fromAmount',
                  errors: [errorMsg]
                },
                { name: 'toAmount', errors: [] }
              ]);
            } else {
              // fromAmount for max toAmount is within fromCurrency limit
              // Show error with calculated fromAmount
              // const errorMsg = `Amount exceeds the maximum allowed limit. Maximum you can pay is ${formatNumber(fromAmountForMaxTo, fromDecimal)} ${fromCurrency?.symbol}`;
              const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
              setFromAmountError(true);
              setToAmountError(false);
              setToAmountMinError("");
              form.setFields([
                {
                  name: 'fromAmount',
                  errors: [errorMsg]
                },
                { name: 'toAmount', errors: [] }
              ]);
            }
          } else {
            setToAmountError(false);
            setToAmountMinError("");
            form.setFields([{ name: 'toAmount', errors: [] }]);
          }
        }
      } else {
        // If configFee not available, only check max limit
        const maxLimitTo = getMaxLimit(toCurrency, true);
        if (maxLimitTo !== null && convertedNum > maxLimitTo) {
          // Calculate backwards from toAmount to fromAmount
          const fromAmountForMaxTo = (maxLimitTo / rate) + conversionFee;

          // Prioritize checking max limit of fromAmount first
          const maxLimitFrom = getMaxLimit(fromCurrency);
          if (maxLimitFrom !== null && fromAmountForMaxTo > maxLimitFrom) {
            // fromAmount for max toAmount exceeds fromCurrency max limit
            // Show fromCurrency max limit error instead
            const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol}`;
            setFromAmountError(true);
            setToAmountError(false);
            setToAmountMinError("");
            form.setFields([
              {
                name: 'fromAmount',
                errors: [errorMsg]
              },
              { name: 'toAmount', errors: [] }
            ]);
          } else {
            // fromAmount for max toAmount is within fromCurrency limit
            // Show error with calculated fromAmount
            // const errorMsg = `Amount exceeds the maximum allowed limit. Maximum you can pay is ${formatNumber(fromAmountForMaxTo, fromDecimal)} ${fromCurrency?.symbol}`;
            const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
            setFromAmountError(true);
            setToAmountError(false);
            setToAmountMinError("");
            form.setFields([
              {
                name: 'fromAmount',
                errors: [errorMsg]
              },
              { name: 'toAmount', errors: [] }
            ]);
          }
        } else {
          setToAmountError(false);
          setToAmountMinError("");
          form.setFields([{ name: 'toAmount', errors: [] }]);
        }
      }

      setIsCalculating(false);
    }

    // All fromAmount validations passed
    setFromAmountError(false);
    form.setFields([{ name: 'fromAmount', errors: [] }]);
  };

  const handleBlurFromAmount = async () => {
    const rawValue = form.getFieldValue('fromAmount');
    const val = Number(rawValue);

    // Clear preserved error when user blurs (user has finished editing)
    preservedFromAmountErrorRef.current = null;

    if (val && val > 0) {
      const formattedValue = val.toFixed(fromDecimal);
      form.setFieldsValue({
        fromAmount: formattedValue,
      });
      setDirection(Direction.PAY_TO_RCV);
      setLastChange('FROM');

      // Always validate fromAmount on blur to ensure errors are shown
      // Validate immediately and then retry after delays to catch configFee state updates
      const performValidation = async () => {
        try {
          // First validate basic rules (required, > 0)
          await form.validateFields(['fromAmount']);

          // Validate min amount first if configFee and configFee are available
          // Skip min amount validation if configFee is not available or loading
          if (!configFee || loadingFee) {
            // If configFee is loading, wait for it to be ready
            // If not changing currency and configFee is not available, trigger a fetch
            if (!isChangingCurrencyRef.current && !configFee && fromCurrency?.id) {
              fetchFee(fromCurrency.id);
            }
            // Don't validate min amount yet, wait for configFee to be ready
            // But still calculate toAmount if basic validation passed
            preservedFromAmountErrorRef.current = null;
          } else {
            // Validate min amount first
            const minFrom = getMinFromAmount();
            if (minFrom !== null && val < minFrom) {
              const errorMsg = `The amount you pay must be at least ${formatNumber(minFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
              preservedFromAmountErrorRef.current = errorMsg;
              setFromAmountError(true);
              form.setFields([{
                name: 'fromAmount',
                errors: [errorMsg]
              }]);
              // Don't calculate toAmount if min amount validation fails
              form.resetFields(['toAmount']);
              return;
            }
            // Clear preserved error since validation passed
            preservedFromAmountErrorRef.current = null;
          }

          // Then validate max limit
          const maxLimit = getMaxLimit(fromCurrency);
          if (maxLimit !== null && maxLimit >= 0 && val > maxLimit) {
            const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimit, fromDecimal)} ${fromCurrency?.symbol || ''}`;
            setFromAmountError(true);
            form.setFields([{
              name: 'fromAmount',
              errors: [errorMsg]
            }]);
            // Don't calculate toAmount if max limit validation fails
            form.resetFields(['toAmount']);
            return;
          }

          const conversionFee = calculateConversionFeeToCurrency(val);
          const val2 = Number(val - conversionFee);
          const fromAmountValue = Number((val2 > 0 ? val2 : 0) * rate);
          // If fromAmount is valid, calculate toAmount
          const converted = Number(fromAmountValue).toFixed(toDecimal);
          const convertedNum = Number(converted);
          // Set toAmount value
          form.setFieldsValue({
            toAmount: convertedNum >= 0 ? converted : null,
          });

          // Validate toAmount: check min first, then max
          // Validate min amount if configFee is available
          if (configFee && !loadingFee) {
            const minTo = getMinToAmount();
            if (minTo !== null && convertedNum < minTo) {
              const errorMsg = `The amount you receive must be at least ${formatNumber(minTo, toDecimal)} ${toCurrency?.symbol || ''}`;
              setToAmountMinError(errorMsg);
              setToAmountError(false);
              form.setFields([{ name: 'toAmount', errors: [errorMsg] }]);
            } else {
              // Then check max limit for toCurrency
              const maxLimitTo = getMaxLimit(toCurrency, true);
              if (maxLimitTo !== null && convertedNum > maxLimitTo) {
                // Calculate backwards from toAmount to fromAmount
                const fromAmountForMaxTo = (maxLimitTo / rate) + conversionFee;

                // Prioritize checking max limit of fromAmount first
                const maxLimitFrom = getMaxLimit(fromCurrency);
                if (maxLimitFrom !== null && fromAmountForMaxTo > maxLimitFrom) {
                  // fromAmount for max toAmount exceeds fromCurrency max limit
                  // Show fromCurrency max limit error instead
                  const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
                  setFromAmountError(true);
                  setToAmountError(false);
                  setToAmountMinError("");
                  form.setFields([
                    {
                      name: 'fromAmount',
                      errors: [errorMsg]
                    },
                    { name: 'toAmount', errors: [] }
                  ]);
                } else {
                  // fromAmount for max toAmount is within fromCurrency limit
                  // Show error with calculated fromAmount
                  // const errorMsg = `Amount exceeds the maximum allowed limit. Maximum you can pay is ${formatNumber(fromAmountForMaxTo, fromDecimal)} ${fromCurrency?.symbol || ''}`;
                  const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
                  setFromAmountError(true);
                  setToAmountError(false);
                  setToAmountMinError("");
                  form.setFields([
                    {
                      name: 'fromAmount',
                      errors: [errorMsg]
                    },
                    { name: 'toAmount', errors: [] }
                  ]);
                }
              } else {
                setToAmountError(false);
                setToAmountMinError("");
                form.setFields([{ name: 'toAmount', errors: [] }]);
              }
            }
          } else {
            // If configFee is not ready, only check max limit
            const maxLimitTo = getMaxLimit(toCurrency, true);
            if (maxLimitTo !== null && convertedNum > maxLimitTo) {
              // Calculate backwards from toAmount to fromAmount
              const fromAmountForMaxTo = (maxLimitTo / rate) + conversionFee;

              // Prioritize checking max limit of fromAmount first
              const maxLimitFrom = getMaxLimit(fromCurrency);
              if (maxLimitFrom !== null && fromAmountForMaxTo > maxLimitFrom) {
                // fromAmount for max toAmount exceeds fromCurrency max limit
                // Show fromCurrency max limit error instead
                const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
                setFromAmountError(true);
                setToAmountError(false);
                setToAmountMinError("");
                form.setFields([
                  {
                    name: 'fromAmount',
                    errors: [errorMsg]
                  },
                  { name: 'toAmount', errors: [] }
                ]);
              } else {
                // fromAmount for max toAmount is within fromCurrency limit
                // Show error with calculated fromAmount
                // const errorMsg = `Amount exceeds the maximum allowed limit. Maximum you can pay is ${formatNumber(fromAmountForMaxTo, fromDecimal)} ${fromCurrency?.symbol || ''}`;
                const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
                setFromAmountError(true);
                setToAmountError(false);
                setToAmountMinError("");
                form.setFields([
                  {
                    name: 'fromAmount',
                    errors: [errorMsg]
                  },
                  { name: 'toAmount', errors: [] }
                ]);
              }
            } else {
              setToAmountError(false);
              setToAmountMinError("");
              // If configFee is not ready, validate after delay
              setTimeout(() => {
                form.validateFields(['toAmount']).catch(() => {
                  // Error is expected if validation fails
                });
              }, 200);
            }
          }
        } catch (error) {
          // If fromAmount has error, don't calculate toAmount
          const fromAmountErrors = form.getFieldError('fromAmount');
          if (fromAmountErrors && fromAmountErrors.length > 0) {
            setFromAmountError(true);
          }
          setToAmountError(false);
          setToAmountMinError("");
          form.resetFields(['toAmount']);
        }
      };

      setTimeout(() => {
        performValidation();
      }, 100);
    } else {
      setToAmountError(false);
      setToAmountMinError("");
      form.resetFields(['toAmount']);
      setFromAmountError(false);
      // Still validate even if value is 0 or invalid to show errors
      form.validateFields(['fromAmount']).catch((error) => {
        // Error is expected if value is invalid
        const fromAmountErrors = form.getFieldError('fromAmount');
        if (fromAmountErrors && fromAmountErrors.length > 0) {
          setFromAmountError(true);
        }
      });
      // Also validate again after a longer delay to catch configFee updates
      setTimeout(() => {
        form.validateFields(['fromAmount']).catch((error) => {
          // Error is expected if value is invalid
          const fromAmountErrors = form.getFieldError('fromAmount');
          if (fromAmountErrors && fromAmountErrors.length > 0) {
            setFromAmountError(true);
          }
        });
      }, 100);
    }
  };

  const handleMaxFromAmount = () => {
    setDirection(Direction.PAY_TO_RCV);
    // Use the selected item's balance as max amount
    const maxAmount = totalBalance || 0;
    const maxAmountFixed = maxAmount.toFixed(fromDecimal);
    form.setFieldValue('fromAmount', maxAmountFixed);

    // Calculate and validate using validateAndCalculateFromAmount to ensure full validation
    validateAndCalculateFromAmount(maxAmountFixed);
  };

  const handleExchange = async (values: any) => {
    try {
      const fromNum = Number(values.fromAmount);
      const toNum = Number(values.toAmount);
      const rateNum = rate > 0 ? Number(rate) : 0;

      let paymentAmount: number | undefined;
      let receiveAmount: number | undefined;

      if (direction === Direction.PAY_TO_RCV && fromNum > 0 && rateNum > 0) {
        // BE: Number((paymentAmount * exchangeRate).toFixed(receiveDecimals)) must equal receiveAmount
        const conversionFee = calculateConversionFeeToCurrency(fromNum);
        const netPayment = Math.max(0, fromNum - conversionFee);
        paymentAmount = Number(netPayment.toFixed(fromDecimal));
        receiveAmount = Number((paymentAmount * rateNum).toFixed(toDecimal));
      } else if (direction === Direction.RCV_TO_PAY && toNum > 0 && rateNum > 0) {
        // BE: Number((receiveAmount / exchangeRate).toFixed(paymentDecimals)) must equal paymentAmount
        receiveAmount = Number(toNum.toFixed(toDecimal));
        paymentAmount = Number((receiveAmount / rateNum).toFixed(fromDecimal));
      }

      const body = {
        paymentAmount,
        receiveAmount,
        receiveCurrencyId: toCurrency?.id,
        paymentCurrencyId: fromCurrency?.id,
        exchangeRate: rateNum > 0 ? rateNum : undefined,
        direction,
        usdToFeeCurrencyRate: configFee?.usdToFeeCurrencyRate,
        feeConfigVersion: configFee?.version.toString(),
      };
      await exchangeData(body);
    } catch (error) {
      console.error("Exchange data failed", error);
    }
  }

  // Synchronous validation function for submit (no timeout)
  const validateAndFormatForSubmit = async () => {
    const fromAmountValue = form.getFieldValue('fromAmount');
    const toAmountValue = form.getFieldValue('toAmount');
    const fromAmountNum = Number(fromAmountValue);
    const toAmountNum = Number(toAmountValue);

    // Determine which field to use as source based on lastChange
    const useFromAsSource = (lastChange === 'FROM' && fromAmountNum > 0) ||
      (fromAmountNum > 0 && toAmountNum <= 0) ||
      (fromAmountNum > 0 && toAmountNum > 0 && lastChange !== 'TO');

    // Validate and format fromAmount first
    if (fromAmountValue && fromAmountNum > 0) {
      // Format fromAmount
      const formattedFrom = fromAmountNum.toFixed(fromDecimal);
      form.setFieldsValue({ fromAmount: formattedFrom });

      // Validate min amount first if configFee is available
      if (configFee && !loadingFee && !loadingFeeToCurrency) {
        const minFrom = getMinFromAmount();
        if (minFrom !== null && fromAmountNum < minFrom) {
          const errorMsg = `The amount you pay must be at least ${formatNumber(minFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
          setFromAmountError(true);
          form.setFields([{
            name: 'fromAmount',
            errors: [errorMsg]
          }]);
          throw new Error(errorMsg);
        }
      }

      // Then validate max limit
      const maxLimit = getMaxLimit(fromCurrency);
      if (maxLimit !== null && maxLimit >= 0 && fromAmountNum > maxLimit) {
        const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimit, fromDecimal)} ${fromCurrency?.symbol || ''}`;
        setFromAmountError(true);
        form.setFields([{
          name: 'fromAmount',
          errors: [errorMsg]
        }]);
        throw new Error(errorMsg);
      }

      // If fromAmount is the source, calculate toAmount
      if (useFromAsSource && fromCurrency && toCurrency && rate > 0) {
        const conversionFee = calculateConversionFeeToCurrency(fromAmountNum);
        const val = Number(fromAmountNum - conversionFee);
        const converted = Number((val > 0 ? val : 0) * rate).toFixed(toDecimal);
        const convertedNum = Number(converted);
        form.setFieldsValue({
          toAmount: convertedNum >= 0 ? converted : null,
        });

        // Validate calculated toAmount: check min first, then max
        if (configFeeToCurrency && !loadingFeeToCurrency) {
          const minTo = getMinToAmount();
          if (minTo !== null && convertedNum < minTo) {
            const errorMsg = `The amount you receive must be at least ${formatNumber(minTo, toDecimal)} ${toCurrency?.symbol || ''}`;
            setToAmountMinError(errorMsg);
            setToAmountError(false);
            form.setFields([{ name: 'toAmount', errors: [errorMsg] }]);
            throw new Error(errorMsg);
          }
        }

        // Then validate max limit for toAmount
        const maxLimitTo = getMaxLimit(toCurrency, true);
        if (maxLimitTo !== null && convertedNum > maxLimitTo) {
          // Calculate backwards from toAmount to fromAmount
          const fromAmountForMaxTo = (maxLimitTo / rate) + conversionFee;

          // Prioritize checking max limit of fromAmount first
          const maxLimitFrom = getMaxLimit(fromCurrency);
          if (maxLimitFrom !== null && fromAmountForMaxTo > maxLimitFrom) {
            // fromAmount for max toAmount exceeds fromCurrency max limit
            // Show fromCurrency max limit error instead
            const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
            setFromAmountError(true);
            setToAmountError(false);
            setToAmountMinError("");
            form.setFields([
              {
                name: 'fromAmount',
                errors: [errorMsg]
              },
              { name: 'toAmount', errors: [] }
            ]);
            throw new Error(errorMsg);
          }

          // fromAmount for max toAmount is within fromCurrency limit
          // Show error with calculated fromAmount
          // const errorMsg = `Amount exceeds the maximum allowed limit. Maximum you can pay is ${formatNumber(fromAmountForMaxTo, fromDecimal)} ${fromCurrency?.symbol || ''}`;
          const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
          setFromAmountError(true);
          setToAmountError(false);
          setToAmountMinError("");
          form.setFields([
            {
              name: 'fromAmount',
              errors: [errorMsg]
            },
            { name: 'toAmount', errors: [] }
          ]);
          throw new Error(errorMsg);
        }
      }
    }

    // Validate and format toAmount
    if (toAmountValue && toAmountNum > 0) {
      // Format toAmount
      const formattedTo = toAmountNum.toFixed(toDecimal);
      form.setFieldsValue({ toAmount: formattedTo });

      // Validate toAmount min limit first
      if (configFeeToCurrency && !loadingFeeToCurrency) {
        const minTo = getMinToAmount();
        if (minTo !== null && toAmountNum < minTo) {
          const errorMsg = `The amount you receive must be at least ${formatNumber(minTo, toDecimal)} ${toCurrency?.symbol || ''}`;
          setToAmountMinError(errorMsg);
          setToAmountError(false);
          form.setFields([{ name: 'toAmount', errors: [errorMsg] }]);
          throw new Error(errorMsg);
        }
      }

      // Then validate toAmount max limit
      const maxLimitTo = getMaxLimit(toCurrency, true);
      if (maxLimitTo !== null && toAmountNum > maxLimitTo) {
        // Calculate backwards from toAmount to fromAmount
        const toFrom = Number(toAmountNum / rate);
        const conversionFee = calculateConversionFeeToCurrency(toFrom);
        const fromAmountForMaxTo = (maxLimitTo / rate) + conversionFee;

        // Prioritize checking max limit of fromAmount first
        const maxLimitFrom = getMaxLimit(fromCurrency);
        if (maxLimitFrom !== null && fromAmountForMaxTo > maxLimitFrom) {
          // fromAmount for max toAmount exceeds fromCurrency max limit
          // Show fromCurrency max limit error instead
          const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
          setFromAmountError(true);
          setToAmountError(false);
          setToAmountMinError("");
          form.setFields([
            {
              name: 'fromAmount',
              errors: [errorMsg]
            },
            { name: 'toAmount', errors: [] }
          ]);
          throw new Error(errorMsg);
        }

        // fromAmount for max toAmount is within fromCurrency limit
        // Show error with calculated fromAmount
        // const errorMsg = `Amount exceeds the maximum allowed limit. Maximum you can pay is ${formatNumber(fromAmountForMaxTo, fromDecimal)} ${fromCurrency?.symbol || ''}`;
        const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
        setFromAmountError(true);
        setToAmountError(false);
        setToAmountMinError("");
        form.setFields([
          {
            name: 'fromAmount',
            errors: [errorMsg]
          },
          { name: 'toAmount', errors: [] }
        ]);
        throw new Error(errorMsg);
      }

      // If toAmount is the source, calculate fromAmount
      if (!useFromAsSource && fromCurrency && toCurrency && rate > 0) {
        const toFrom = Number(toAmountNum / rate);
        const conversionFee = calculateConversionFeeToCurrency(toFrom);
        const calculatedFromAmount = Number(toFrom + conversionFee).toFixed(fromDecimal);
        const calculatedFromNum = Number(calculatedFromAmount);
        form.setFieldsValue({
          fromAmount: calculatedFromAmount,
        });

        // Validate calculated fromAmount: check min first, then max
        if (configFee && !loadingFee && !loadingFeeToCurrency) {
          const minFrom = getMinFromAmount();
          if (minFrom !== null && calculatedFromNum < minFrom) {
            const errorMsg = `The amount you pay must be at least ${formatNumber(minFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
            setFromAmountError(true);
            form.setFields([{
              name: 'fromAmount',
              errors: [errorMsg]
            }]);
            throw new Error(errorMsg);
          }
        }

        // Then validate max limit
        const maxLimit = getMaxLimit(fromCurrency);
        if (maxLimit !== null && maxLimit >= 0 && calculatedFromNum > maxLimit) {
          const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimit, fromDecimal)} ${fromCurrency?.symbol || ''}`;
          setFromAmountError(true);
          form.setFields([{
            name: 'fromAmount',
            errors: [errorMsg]
          }]);
          throw new Error(errorMsg);
        }
      }
    }

    // Clear errors if validation passed
    setFromAmountError(false);
    setToAmountError(false);
    setToAmountMinError("");
  };

  const handleSubmit = async (values?: any) => {
    try {
      // First, validate and format synchronously (no timeout)
      await validateAndFormatForSubmit();

      // Then validate with form rules
      await form.validateFields(['fromAmount', 'toAmount']);

      // Get latest values after formatting
      const finalValues = form.getFieldsValue();
      const fromAmountErrors = form.getFieldError('fromAmount');
      const toAmountErrors = form.getFieldError('toAmount');

      // Double check for errors (in case form validation didn't catch them)
      if (fromAmountErrors && fromAmountErrors.length > 0) {
        throw new Error(fromAmountErrors[0]);
      }
      if (toAmountErrors && toAmountErrors.length > 0) {
        throw new Error(toAmountErrors[0]);
      }
      if (fromAmountError || toAmountError || toAmountMinError) {
        throw new Error('Validation failed');
      }

      await handleExchange(finalValues);
    } catch (error: any) {
      console.error("Validate fields failed", error);
      // Error is already set in form fields by validateAndFormatForSubmit
    }
  };

  const findCurrencyBySymbol = (dataAllCurrency: any[], symbol: string) => {
    return dataAllCurrency.find((item: any) => item.symbol === symbol) as any;
  };

  const pickFallbackCurrency = (currencies: any[], excludeId?: number) => {
    if (!currencies || currencies.length === 0) return undefined;
    const filtered = excludeId
      ? currencies.filter((item: any) => item.id !== excludeId)
      : currencies;

    // Prefer USDT, then ETH, otherwise first available
    return (
      findCurrencyBySymbol(filtered, 'USDT') ||
      findCurrencyBySymbol(filtered, 'ETH') ||
      filtered[0]
    );
  };

  const getMinFromAmount = () => {
    // Min fromAmount = (min of toCurrency converted to fromCurrency) + fee
    if (!configFee) return null;
    const minToCurrency = configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate;

    // Total min = converted min + fee
    const totalMin = minToCurrency;
    return Number(
      roundTo(
        totalMin,
        fromDecimal
      )
    );
  };

  const getMinToAmount = () => {
    // Min toAmount = min transfer amount of toCurrency
    if (!configFeeToCurrency) return null;
    const minToCurrency = configFeeToCurrency?.bufferUsd * configFeeToCurrency?.usdToFeeCurrencyRate;

    return Number(
      roundTo(
        minToCurrency,
        toDecimal
      )
    );
  };

  // Initialize currencies when dataCurrencyToken is available
  useEffect(() => {
    if (filteredCurrencyTokens && filteredCurrencyTokens.length > 0) {
      // Initialize fromCurrency based on selectedItem
      if (selectedItem) {
        const selectedToken = filteredCurrencyTokens.find((token: any) => token.id === selectedItem.id);
        if (selectedToken) {
          setFromCurrency(selectedToken);
          setFromDecimal(selectedToken.decimals || 6);
          form.setFieldValue('fromCurrency', selectedToken?.id);
        } else {
          const fallbackCurrency = pickFallbackCurrency(filteredCurrencyTokens);
          setFromCurrency(fallbackCurrency);
          setFromDecimal(fallbackCurrency?.decimals || 6);
          form.setFieldValue('fromCurrency', fallbackCurrency?.id);
        }
      } else {
        // No selectedItem, use a sensible default
        const fallbackCurrency = pickFallbackCurrency(filteredCurrencyTokens);
        setFromCurrency(fallbackCurrency);
        setFromDecimal(fallbackCurrency?.decimals || 6);
        form.setFieldValue('fromCurrency', fallbackCurrency?.id);
      }

      // Initialize toCurrency to a different currency than fromCurrency
      const currentFromId = selectedItem?.id || filteredCurrencyTokens[0]?.id;
      const fallbackToCurrency = pickFallbackCurrency(
        filteredCurrencyTokens,
        currentFromId
      );
      setToCurrency(fallbackToCurrency);
      setToDecimal(fallbackToCurrency?.decimals || 2);
      form.setFieldValue('toCurrency', fallbackToCurrency?.id);
    }
  }, [filteredCurrencyTokens, selectedItem]);

  // Update decimals when currencies change
  useEffect(() => {
    if (fromCurrency) {
      setFromDecimal(fromCurrency.decimals || 6);
    }
  }, [fromCurrency]);

  useEffect(() => {
    if (toCurrency) {
      setToDecimal(toCurrency.decimals || 2);
    }
  }, [toCurrency]);

  // Pull rate from API
  useEffect(() => {
    if (!rateData || isCalculating) return;
    const apiRate = Number(rateData?.data?.data || 0);
    if (apiRate > 0) {
      setRate(apiRate);
      // Recalculate based on current direction when rate changes
      if (direction === Direction.PAY_TO_RCV) {
        // Recalculate toAmount when rate changes using fromAmount as the source
        const fromAmountValue = form.getFieldValue('fromAmount');
        // Don't calculate if fromAmount has errors
        const fromAmountErrors = form.getFieldError('fromAmount');
        if (fromAmountErrors && fromAmountErrors.length > 0) return;
        if (fromAmountValue && Number(fromAmountValue) > 0 && fromCurrency && toCurrency) {
          setIsCalculating(true);
          const conversionFee = calculateConversionFeeToCurrency(Number(fromAmountValue));
          const val = Number(fromAmountValue - conversionFee);
          const converted = (((val > 0 ? val : 0) * apiRate)).toFixed(toDecimal);
          form.setFieldValue('toAmount', Number(converted) >= 0 ? converted : null);
          setIsCalculating(false);
        }
      } else if (direction === Direction.RCV_TO_PAY) {
        // Recalculate fromAmount when rate changes using toAmount as the source
        const toAmountValue = form.getFieldValue('toAmount');
        // Don't calculate if toAmount has errors
        const toAmountErrors = form.getFieldError('toAmount');
        if (toAmountErrors && toAmountErrors.length > 0) return;
        if (toAmountValue && Number(toAmountValue) > 0 && fromCurrency && toCurrency) {
          setIsCalculating(true);
          const val = Number(toAmountValue / apiRate);
          const conversionFee = calculateConversionFeeToCurrency(Number(val));
          const converted = Number((val + conversionFee)).toFixed(fromDecimal);
          form.setFieldValue('fromAmount', Number(converted) >= 0 ? converted : null);
          setIsCalculating(false);
        }
      }
    }
  }, [rateData, fromCurrency, toCurrency, toDecimal, fromDecimal, direction, form, isCalculating]);

  // Refresh rate every 60s while modal open and currencies selected
  useEffect(() => {
    if (!selectedItem || !fromCurrency?.id || !toCurrency?.id) return;
    const id = setInterval(() => {
      refetchRate();
    }, 60000);
    return () => clearInterval(id);
  }, [selectedItem, fromCurrency?.id, toCurrency?.id, refetchRate]);

  // Recalculate when currencies or rate change based on current direction (but don't validate - validation only on blur/max)
  useEffect(() => {
    // Don't recalculate if we're changing currency (format is already handled in handleChange functions)
    if (isChangingCurrencyRef.current) {
      return;
    }

    if (isCalculating) return;
    if (direction === Direction.PAY_TO_RCV || lastChange === 'FROM') {
      // Recalculate toAmount if there's a valid fromAmount value
      const fromAmountValue = form.getFieldValue('fromAmount');
      // Don't calculate if fromAmount has errors
      const fromAmountErrors = form.getFieldError('fromAmount');
      if (fromAmountErrors && fromAmountErrors.length > 0) return;
      if (fromAmountValue && Number(fromAmountValue) > 0 && fromCurrency && toCurrency && rate > 0) {
        setIsCalculating(true);
        const conversionFee = calculateConversionFeeToCurrency(Number(fromAmountValue));
        const val = Number(fromAmountValue - conversionFee);
        const converted = (((val > 0 ? val : 0) * rate)).toFixed(toDecimal);
        form.setFieldValue('toAmount', Number(converted) >= 0 ? converted : null);
        setIsCalculating(false);
      }
    } else if (direction === Direction.RCV_TO_PAY || lastChange === 'TO') {
      // Recalculate fromAmount if there's a valid toAmount value
      const toAmountValue = form.getFieldValue('toAmount');
      // Don't calculate if toAmount has errors
      const toAmountErrors = form.getFieldError('toAmount');
      if (toAmountErrors && toAmountErrors.length > 0) return;
      if (toAmountValue && Number(toAmountValue) > 0 && fromCurrency && toCurrency && rate > 0) {
        setIsCalculating(true);
        const val = Number(toAmountValue);
        const fromConverted = Number((val / rate));
        const conversionFee = calculateConversionFeeToCurrency(Number(fromConverted));
        const converted = Number(fromConverted + conversionFee).toFixed(fromDecimal);
        form.setFieldValue('fromAmount', Number(converted) >= 0 ? converted : null);
        setIsCalculating(false);
      }
    }
  }, [
    fromCurrency?.id,
    toCurrency?.id,
    rate,
    toDecimal,
    fromDecimal,
    direction,
    lastChange,
    isCalculating,
    configFee,
  ]);

  useEffect(() => {
    if (!fromCurrency) return
    fetchFee(fromCurrency?.id)
    const intervalId = setInterval(() => {
      fetchFee(fromCurrency?.id)
    }, 60000)
    return () => clearInterval(intervalId)
  }, [fromCurrency, selectedItem])

  useEffect(() => {
    if (!toCurrency?.id) return
    fetchFeeToCurrency(toCurrency.id)
    const intervalId = setInterval(() => {
      fetchFeeToCurrency(toCurrency.id)
    }, 60000)
    return () => clearInterval(intervalId)
  }, [toCurrency, selectedItem])

  // Note: Validation is only done on blur, not real-time
  // Re-validate fromAmount when configFee or configFee changes
  useEffect(() => {
    // Only validate if we have a value in fromAmount and configFee is ready
    const fromAmountValue = form.getFieldValue('fromAmount');
    if (!fromAmountValue || !fromCurrency || !toCurrency || !rate || rate <= 0) return;

    const val = Number(fromAmountValue);
    if (isNaN(val) || val <= 0) return;

    // Skip if configFee is not ready yet
    if (!configFee || loadingFee || !configFee || loadingFeeToCurrency) return;

    // Skip if we're currently changing currency
    if (isChangingCurrencyRef.current) return;

    // Skip if user is currently typing in fromAmount (only validate on blur)
    if (isFromAmountFocusedRef.current) return;

    // Use setTimeout to ensure state is updated
    const timeoutId = setTimeout(() => {
      // Validate min amount first
      const minFrom = getMinFromAmount();
      if (minFrom !== null && val < minFrom) {
        const errorMsg = `The amount you pay must be at least ${formatNumber(minFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
        setFromAmountError(true);
        form.setFields([{
          name: 'fromAmount',
          errors: [errorMsg]
        }]);
        return;
      }

      // Then validate max limit
      const maxLimit = getMaxLimit(fromCurrency);
      if (maxLimit !== null && maxLimit > 0 && val > maxLimit) {
        const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimit, fromDecimal)} ${fromCurrency?.symbol || ''}`;
        setFromAmountError(true);
        form.setFields([{
          name: 'fromAmount',
          errors: [errorMsg]
        }]);
        return;
      }

      // Validation passed - clear errors
      setFromAmountError(false);
      form.setFields([{ name: 'fromAmount', errors: [] }]);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [configFee, configFee, loadingFee, loadingFeeToCurrency, fromCurrency, toCurrency, rate, fromDecimal]);

  // Re-validate toAmount when configFee changes
  useEffect(() => {
    // Only validate if we have a value in toAmount and configFee is ready
    const toAmountValue = form.getFieldValue('toAmount');
    if (!toAmountValue || !fromCurrency || !toCurrency || !rate || rate <= 0) return;

    const val = Number(toAmountValue);
    if (isNaN(val) || val <= 0) return;

    // Skip if configFee is not ready yet
    if (!configFee || loadingFeeToCurrency) return;

    // Skip if we're currently changing currency
    if (isChangingCurrencyRef.current) return;

    // Skip if user is currently typing in toAmount (only validate on blur)
    if (isToAmountFocusedRef.current) return;

    // If direction is PAY_TO_RCV, check if fromAmount has errors first
    // Only validate toAmount if fromAmount is valid
    if (direction === Direction.PAY_TO_RCV) {
      const fromAmountErrors = form.getFieldError('fromAmount');
      if (fromAmountErrors && fromAmountErrors.length > 0) {
        // fromAmount has errors, don't validate toAmount
        return;
      }

      const fromAmountValue = form.getFieldValue('fromAmount');
      if (!fromAmountValue || Number(fromAmountValue) <= 0) {
        // fromAmount is invalid, don't validate toAmount
        return;
      }
    }

    // Use setTimeout to ensure state is updated
    const timeoutId = setTimeout(() => {
      // Validate min amount first
      const minTo = getMinToAmount();
      if (minTo !== null && val < minTo) {
        const errorMsg = `The amount you receive must be at least ${formatNumber(minTo, toDecimal)} ${toCurrency?.symbol || ''}`;
        setToAmountMinError(errorMsg);
        setToAmountError(false);
        form.setFields([{
          name: 'toAmount',
          errors: [errorMsg]
        }]);
        return;
      }

      // Then validate max limit - prioritize showing error in fromAmount
      const maxLimit = getMaxLimit(toCurrency, true);
      if (maxLimit !== null && val > maxLimit) {
        // Calculate backwards from toAmount to fromAmount
        const toFrom = Number(val / rate);
        const conversionFee = calculateConversionFeeToCurrency(toFrom);
        const fromAmountForMaxTo = (maxLimit / rate) + conversionFee;

        // Prioritize checking max limit of fromAmount first
        const maxLimitFrom = getMaxLimit(fromCurrency);
        if (maxLimitFrom !== null && fromAmountForMaxTo > maxLimitFrom) {
          // fromAmount for max toAmount exceeds fromCurrency max limit
          // Show fromCurrency max limit error instead
          const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
          setFromAmountError(true);
          setToAmountError(false);
          setToAmountMinError("");
          form.setFields([
            {
              name: 'fromAmount',
              errors: [errorMsg]
            },
            { name: 'toAmount', errors: [] }
          ]);
          return;
        }

        // fromAmount for max toAmount is within fromCurrency limit
        // Show error with calculated fromAmount
        // const errorMsg = `Amount exceeds the maximum allowed limit. Maximum you can pay is ${formatNumber(fromAmountForMaxTo, fromDecimal)} ${fromCurrency?.symbol || ''}`;
        const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
        setFromAmountError(true);
        setToAmountError(false);
        setToAmountMinError("");
        form.setFields([
          {
            name: 'fromAmount',
            errors: [errorMsg]
          },
          { name: 'toAmount', errors: [] }
        ]);
        return;
      }

      // Validation passed - clear errors
      setToAmountError(false);
      setToAmountMinError("");
      form.setFields([{ name: 'toAmount', errors: [] }]);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [configFee, loadingFeeToCurrency, configFeeToCurrency, loadingFee, fromCurrency, toCurrency, rate, toDecimal, direction]);

  useEffect(() => {
    if (!avlBalance) return;
    setTotalBalance(avlBalance.data?.data?.totalBalance || 0);
    setDecimals(avlBalance?.data?.data?.decimals || 0);
    if (avlBalance.data?.data?.totalBalance === 0) {
      form.setFieldValue('fromAmount', null);
      form.setFieldValue('toAmount', null);
      setFromAmountError(false);
      setToAmountError(false);
      setToAmountMinError("");
      form.setFields([{ name: 'fromAmount', errors: [] }, { name: 'toAmount', errors: [] }]);
    }
    const avlBalanceData = setInterval(() => {
      refetch();
    }, 60000);
    return () => clearInterval(avlBalanceData);
  }, [avlBalance, loadingAvlBalance]);

  const handleChangeFromCurrency = async (value: any) => {
    // Mark that we're changing currency to prevent useEffect from recalculating
    isChangingCurrencyRef.current = true;

    // IMPORTANT: Preserve toAmount error BEFORE clearing configFee
    const toAmountErrors = form.getFieldError('toAmount');
    const hasMinAmountError = toAmountErrors.some((err: string) => err?.includes('must be at least'));
    if (hasMinAmountError) {
      preservedToAmountErrorRef.current = toAmountErrors[0] || null;
    } else {
      preservedToAmountErrorRef.current = null;
    }

    const selected = filteredCurrencyTokens.find(
      (item: any) => item.id === value
    );
    if (!selected) return;

    // Clear configFee immediately when changing currency to prevent validation with old fee data
    setConfigFee(null);

    const newFromDecimal = selected.decimals || 6;
    setFromCurrency(selected);
    setFromDecimal(newFromDecimal);

    const nextTo = toCurrency;
    const nextToDecimal = toDecimal;

    // // Avoid same currency on both sides; pick a fallback if needed
    // if (toCurrency?.id === selected.id) {
    //   const fallbackTo = pickFallbackCurrency(
    //     filteredCurrencyTokens,
    //     selected.id
    //   );
    //   setToCurrency(fallbackTo);
    //   nextTo = fallbackTo;
    //   nextToDecimal = fallbackTo?.decimals || 2;
    //   setToDecimal(nextToDecimal);
    //   form.setFieldValue('toCurrency', fallbackTo?.id);
    // }

    // Save current values
    const currentFromAmount = form.getFieldValue('fromAmount');
    const currentToAmount = form.getFieldValue('toAmount');
    const fromAmountNum = Number(currentFromAmount) || 0;
    const toAmountNum = Number(currentToAmount) || 0;

    // Determine which field to keep based on lastChange (default to FROM if both have values or neither has value)
    const keepFrom = (lastChange === 'FROM' && fromAmountNum > 0) ||
      (fromAmountNum > 0 && toAmountNum <= 0) ||
      (fromAmountNum > 0 && toAmountNum > 0 && lastChange !== 'TO');
    const keepTo = lastChange === 'TO' && toAmountNum > 0 && fromAmountNum <= 0;

    // Format and validate decimals for both fields
    const formatAndValidateDecimals = (value: number, decimals: number) => {
      if (value <= 0) return null;
      const formatted = Number(value.toFixed(decimals));
      return formatted;
    };

    // Wait for new exchange rate to be fetched
    // Note: rate will be updated by useEffect when rateData changes

    // Recalculate based on which field to keep
    if (keepFrom && fromAmountNum > 0) {
      // Keep fromAmount and recalculate toAmount
      // Format fromAmount with new decimals first
      const formattedFromAmount = formatAndValidateDecimals(fromAmountNum, newFromDecimal);
      if (formattedFromAmount !== null) {
        form.setFieldValue('fromAmount', formattedFromAmount.toFixed(newFromDecimal));
      }

      // Don't calculate if fromAmount has errors
      const fromAmountErrors = form.getFieldError('fromAmount');
      if (!(fromAmountErrors && fromAmountErrors.length > 0) && selected && nextTo && rate > 0) {
        const conversionFee = calculateConversionFeeToCurrency(Number(fromAmountNum));
        const val = Number(fromAmountNum - conversionFee);
        const converted = Number((val > 0 ? val : 0) * rate);
        const convertedNum = Number(converted);
        const formattedToAmount = formatAndValidateDecimals(convertedNum, nextToDecimal);
        form.setFieldValue('toAmount', formattedToAmount !== null && formattedToAmount >= 0 ? formattedToAmount.toFixed(nextToDecimal) : 0);
      }
    } else if (keepTo && toAmountNum > 0) {
      // Keep toAmount and recalculate fromAmount
      // Format toAmount with new decimals first
      const formattedToAmount = formatAndValidateDecimals(toAmountNum, nextToDecimal);
      if (formattedToAmount !== null) {
        form.setFieldValue('toAmount', formattedToAmount.toFixed(nextToDecimal));
      }

      // Don't calculate if toAmount has errors
      const toAmountErrors = form.getFieldError('toAmount');
      if (!(toAmountErrors && toAmountErrors.length > 0) && selected && nextTo && rate > 0) {
        const converted = Number((toAmountNum / rate));
        const conversionFee = calculateConversionFeeToCurrency(Number(converted));
        const convertedNum = Number(converted + conversionFee);
        const formattedFromAmount = formatAndValidateDecimals(convertedNum, newFromDecimal);
        form.setFieldValue('fromAmount', formattedFromAmount !== null && formattedFromAmount >= 0 ? formattedFromAmount.toFixed(newFromDecimal) : 0);
      }
    } else {
      // Default: use fromAmount if it has value, otherwise clear both
      if (fromAmountNum > 0) {
        const formattedFromAmount = formatAndValidateDecimals(fromAmountNum, newFromDecimal);
        if (formattedFromAmount !== null) {
          form.setFieldValue('fromAmount', formattedFromAmount.toFixed(newFromDecimal));
        }
        // Recalculate toAmount
        const fromAmountErrors = form.getFieldError('fromAmount');
        if (!(fromAmountErrors && fromAmountErrors.length > 0) && selected && nextTo && rate > 0) {
          const conversionFee = calculateConversionFeeToCurrency(Number(fromAmountNum));
          const val = Number(fromAmountNum - conversionFee);
          const converted = Number((val > 0 ? val : 0) * rate);
          const convertedNum = Number(converted);
          const formattedToAmount = formatAndValidateDecimals(convertedNum, nextToDecimal);
          form.setFieldValue('toAmount', formattedToAmount !== null && formattedToAmount >= 0 ? formattedToAmount.toFixed(nextToDecimal) : 0);
        }
      } else {
        // Clear both if no valid value
        form.setFieldValue('fromAmount', null);
        form.setFieldValue('toAmount', null);
      }
    }

    // Refresh available balance for the newly selected currency
    refetch();

    // // Fetch fee for new fromCurrency
    // if (selected?.id) {
    //   fetchFee(selected.id);
    // }

    // Validate after currency change
    setTimeout(async () => {
      // Validate decimals and values for both fields
      const currentFromValue = form.getFieldValue('fromAmount');
      const currentToValue = form.getFieldValue('toAmount');

      if (currentFromValue) {
        const fromVal = Number(currentFromValue);
        if (fromVal > 0) {
          // Validate fromAmount decimals
          const converted = Number(fromVal);
          const formattedFrom = Number(converted.toFixed(newFromDecimal));
          if (formattedFrom !== fromVal) {
            form.setFieldValue('fromAmount', formattedFrom.toFixed(newFromDecimal));
          }
          try {
            await form.validateFields(['fromAmount']);
          } catch (error) {
            // Error is expected if validation fails
          }
        }
      }

      if (currentToValue) {
        const toVal = Number(currentToValue);
        if (toVal > 0) {
          // Validate toAmount decimals
          const conversionFee = calculateConversionFeeToCurrency(Number(toVal));
          const converted = Number(toVal - conversionFee);
          const formattedTo = Number((converted > 0 ? converted : 0).toFixed(nextToDecimal));
          if (formattedTo !== toVal) {
            form.setFieldValue('toAmount', formattedTo.toFixed(nextToDecimal));
          }
          setTimeout(() => {
            form.validateFields(['toAmount']).catch(() => {
              // Error is expected if validation fails
            });
          }, 150);
        }
      }

      // Reset flag after validation completes
      setTimeout(() => {
        isChangingCurrencyRef.current = false;

        // Re-validate to ensure errors are still displayed if values are still invalid
        if (currentFromValue) {
          form.validateFields(['fromAmount']);
        }

        if (currentToValue) {
          // Wait for configFee to be fetched
          setTimeout(() => {
            form.validateFields(['toAmount']);
          }, 200);
        }
      }, 50);
    }, 100);
  };

  const handleChangeToCurrency = async (value: number) => {
    // Mark that we're changing currency to prevent useEffect from recalculating
    isChangingCurrencyRef.current = true;

    // IMPORTANT: Preserve toAmount error BEFORE clearing configFee
    const toAmountErrors = form.getFieldError('toAmount');
    const hasMinAmountError = toAmountErrors.some((err: string) => err?.includes('must be at least'));
    if (hasMinAmountError) {
      preservedToAmountErrorRef.current = toAmountErrors[0] || null;
    } else {
      preservedToAmountErrorRef.current = null;
    }

    const selected = filteredCurrencyTokens.find(
      (item: any) => item.id === value
    );
    if (!selected) return;

    const nextToDecimal = selected?.decimals || 2;
    setToCurrency(selected);
    setToDecimal(nextToDecimal);
    form.setFieldValue('toCurrency', selected?.id);

    // Save current values
    const currentFromAmount = form.getFieldValue('fromAmount');
    const currentToAmount = form.getFieldValue('toAmount');
    const fromAmountNum = Number(currentFromAmount) || 0;
    const toAmountNum = Number(currentToAmount) || 0;

    // Determine which field to keep based on lastChange (default to FROM if both have values or neither has value)
    const keepFrom = (lastChange === 'FROM' && fromAmountNum > 0) ||
      (fromAmountNum > 0 && toAmountNum <= 0) ||
      (fromAmountNum > 0 && toAmountNum > 0 && lastChange !== 'TO');
    const keepTo = lastChange === 'TO' && toAmountNum > 0 && fromAmountNum <= 0;

    // Format and validate decimals for both fields
    const formatAndValidateDecimals = (value: number, decimals: number) => {
      if (value <= 0) return null;
      const formatted = Number(value.toFixed(decimals));
      return formatted;
    };

    // Recalculate based on which field to keep
    if (keepFrom && fromAmountNum > 0) {
      // Keep fromAmount and recalculate toAmount
      // Format fromAmount with current decimals first
      const formattedFromAmount = formatAndValidateDecimals(fromAmountNum, fromDecimal);
      if (formattedFromAmount !== null) {
        form.setFieldValue('fromAmount', formattedFromAmount.toFixed(fromDecimal));
      }

      // Don't calculate if fromAmount has errors
      const fromAmountErrors = form.getFieldError('fromAmount');
      if (!(fromAmountErrors && fromAmountErrors.length > 0) && fromCurrency && selected && rate > 0) {
        const conversionFee = calculateConversionFeeToCurrency(Number(fromAmountNum));
        const val = Number(fromAmountNum - conversionFee);
        const converted = Number((val > 0 ? val : 0) * rate);
        const convertedNum = Number(converted);
        const formattedToAmount = formatAndValidateDecimals(convertedNum, nextToDecimal);
        form.setFieldValue('toAmount', formattedToAmount !== null && formattedToAmount >= 0 ? formattedToAmount.toFixed(nextToDecimal) : 0);
      }
    } else if (keepTo && toAmountNum > 0) {
      // Keep toAmount and recalculate fromAmount
      // Format toAmount with new decimals first
      const formattedToAmount = formatAndValidateDecimals(toAmountNum, nextToDecimal);
      if (formattedToAmount !== null) {
        form.setFieldValue('toAmount', formattedToAmount.toFixed(nextToDecimal));
      }

      // Don't calculate if toAmount has errors
      const toAmountErrors = form.getFieldError('toAmount');
      if (!(toAmountErrors && toAmountErrors.length > 0) && fromCurrency && selected && rate > 0) {
        const converted = Number((toAmountNum / rate));
        const conversionFee = calculateConversionFeeToCurrency(Number(converted));
        const convertedNum = Number(converted + conversionFee);
        const formattedFromAmount = formatAndValidateDecimals(convertedNum, fromDecimal);
        form.setFieldValue('fromAmount', formattedFromAmount !== null && formattedFromAmount >= 0 ? formattedFromAmount.toFixed(fromDecimal) : 0);
      }
    } else {
      // Default: use fromAmount if it has value, otherwise clear both
      if (fromAmountNum > 0) {
        const formattedFromAmount = formatAndValidateDecimals(fromAmountNum, fromDecimal);
        if (formattedFromAmount !== null) {
          form.setFieldValue('fromAmount', formattedFromAmount.toFixed(fromDecimal));
        }
        // Recalculate toAmount
        const fromAmountErrors = form.getFieldError('fromAmount');
        if (!(fromAmountErrors && fromAmountErrors.length > 0) && fromCurrency && selected && rate > 0) {
          const conversionFee = calculateConversionFeeToCurrency(Number(fromAmountNum));
          const val = Number(fromAmountNum - conversionFee);
          const converted = Number((val > 0 ? val : 0) * rate);
          const convertedNum = Number(converted);
          const formattedToAmount = formatAndValidateDecimals(convertedNum, nextToDecimal);
          form.setFieldValue('toAmount', formattedToAmount !== null && formattedToAmount >= 0 ? formattedToAmount.toFixed(nextToDecimal) : 0);
        }
      } else {
        // Clear both if no valid value
        form.setFieldValue('fromAmount', null);
        form.setFieldValue('toAmount', null);
      }
    }

    // Validate after currency change
    setTimeout(async () => {
      // Validate decimals and values for both fields
      const currentFromValue = form.getFieldValue('fromAmount');
      const currentToValue = form.getFieldValue('toAmount');

      if (currentFromValue) {
        const fromVal = Number(currentFromValue);
        if (fromVal > 0) {
          // Validate fromAmount decimals
          const formattedFrom = Number(fromVal.toFixed(fromDecimal));
          if (formattedFrom !== fromVal) {
            form.setFieldValue('fromAmount', formattedFrom.toFixed(fromDecimal));
          }
          try {
            await form.validateFields(['fromAmount']);
          } catch (error) {
            // Error is expected if validation fails
          }
        }
      }

      if (currentToValue) {
        const toVal = Number(currentToValue);
        if (toVal > 0) {
          // Validate toAmount decimals
          const formattedTo = Number(toVal.toFixed(nextToDecimal));
          if (formattedTo !== toVal) {
            form.setFieldValue('toAmount', formattedTo.toFixed(nextToDecimal));
          }
          setTimeout(() => {
            form.validateFields(['toAmount']).catch(() => {
              // Error is expected if validation fails
            });
          }, 150);
        }
      }

      // Reset flag after validation completes
      setTimeout(() => {
        isChangingCurrencyRef.current = false;

        // Re-validate to ensure errors are still displayed if values are still invalid
        if (currentFromValue) {
          form.validateFields(['fromAmount']);
        }
        if (currentToValue) {
          // Wait for configFee to be fetched
          const validateToAmount = () => {
            if (configFee && !loadingFeeToCurrency) {
              form.validateFields(['toAmount']).then(() => {
                // Clear preserved error if validation passes
                preservedToAmountErrorRef.current = null;
              }).catch(() => {
                // Error is expected if value is invalid
              });
              return true;
            } else {
              return false;
            }
          };

          // Try validation after a delay
          setTimeout(() => {
            if (!validateToAmount()) {
              // Retry after a bit more time if configFee is not ready
              setTimeout(() => {
                if (!validateToAmount()) {
                  // Final retry after even more time
                  setTimeout(() => {
                    validateToAmount();
                  }, 300);
                }
              }, 300);
            }
          }, 200);
        }
      }, 50);
    }, 100);
  };

  useEffect(() => {
    if (!selectedItem) return;
    const handleWalletUpdated = () => {
      refetch();
    };
    socket.on(`user.wallet.updated`, handleWalletUpdated);

    return () => {
      socket.off(`user.wallet.updated`, handleWalletUpdated);
    };
  }, [socket, refetch, selectedItem]);

  const handleBlurToAmount = async () => {
    const rawValue = form.getFieldValue('toAmount');
    const val = Number(rawValue);

    // Clear preserved error when user blurs (user has finished editing)
    preservedToAmountErrorRef.current = null;

    if (val && val > 0) {
      const formattedValue = val.toFixed(toDecimal);
      form.setFieldsValue({ toAmount: formattedValue });
      setDirection(Direction.RCV_TO_PAY);
      setLastChange('TO');

      // Validate toAmount first
      try {
        await form.validateFields(['toAmount']);

        // If toAmount is valid, calculate fromAmount
        const toFrom = Number(val / rate);
        const conversionFee = calculateConversionFeeToCurrency(toFrom);
        const calculatedFromAmount = Number(toFrom + conversionFee).toFixed(fromDecimal);

        form.setFieldsValue({
          fromAmount: calculatedFromAmount,
        });
        // Validate to show error - use multiple timeouts to catch configFee state updates
        setTimeout(() => {
          form.validateFields(['fromAmount']);
        }, 100);
      } catch (error) {
        // If toAmount has error, don't calculate fromAmount
        setFromAmountError(false)
        form.resetFields(['fromAmount']);
        form.setFields([{ name: 'fromAmount', errors: [] }]);
      }
    } else {
      setFromAmountError(false)
      form.resetFields(['fromAmount']);
    }
  };

  const handleContinueReview = () => {
    fetchFee(fromCurrency?.id)
    setShowReviewFee(false)
  }

  const fromAmountValue = watchedFromAmount;
  const toAmountValue = watchedToAmount;
  const fromAmountNum = Number(fromAmountValue);
  const toAmountNum = Number(toAmountValue);

  const useFromAsSource =
    (lastChange === 'FROM' && fromAmountNum > 0) ||
    (fromAmountNum > 0 && toAmountNum <= 0) ||
    (fromAmountNum > 0 && toAmountNum > 0 && lastChange !== 'TO');

  const hasValidSourceAmount = useFromAsSource
    ? fromAmountNum > 0
    : toAmountNum > 0;

  // Use parsed numbers: Number.isFinite is false for numeric strings; getFieldValue can be stale on render.
  const isEitherAmountZeroOrInvalid =
    !Number.isFinite(fromAmountNum) ||
    !Number.isFinite(toAmountNum) ||
    fromAmountNum === 0 ||
    toAmountNum === 0;

  const isSubmitDisabled =
    !fromCurrency ||
    !toCurrency ||
    !hasValidSourceAmount ||
    isEitherAmountZeroOrInvalid ||
    fromAmountError ||
    toAmountError ||
    !!toAmountMinError ||
    isCalculating ||
    loadingRate ||
    loadingFee ||
    loadingFeeToCurrency ||
    loadingFeeConfig ||
    loadingExchangeData;

  return (
    <>
      {selectedItem ? (
        <>
          <Modal
            open={!!selectedItem}
            onCancel={handleCancelModal}
            footer={null}
            destroyOnClose
            maskClosable={false}
            title={
              <>
                <Typography
                  style={{
                    fontSize: isTablet ? 16 : 20,
                    fontWeight: 700,
                    color: "#C2C6CE",
                  }}
                >
                  {"Exchange Money"}
                </Typography>
                <CloseIcon
                  onClick={handleCancelModal}
                  className="icon-animation"
                />
              </>
            }
            className="exchange-modal"
          >
            <Form form={form} onFinish={handleSubmit}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: isTablet ? "18px" : "24px",
                  height: "100% ",
                  padding: isTablet ? "16px 16px 0px 16px" : "24px 24px 0px 24px",
                }}
              >
                {/* From Section */}
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography.Text
                      style={{
                        fontSize: isTablet ? 14 : 16,
                        fontWeight: 500,
                        color: "white",
                        marginBottom: 8,
                        display: "block",
                      }}
                    >
                      From
                    </Typography.Text>
                    <Typography.Text
                      style={{
                        fontSize: isTablet ? 12 : 14,
                        fontWeight: 400,
                        color: "white",
                      }}
                    >
                      Avl. balance:{" "}
                      <Typography.Text
                        style={{
                          fontSize: isTablet ? 12 : 14,
                          fontWeight: 400,
                          color: "white",
                        }}
                      >
                        {formatNumber(
                          Number(totalBalance || 0),
                          decimals || selectedItem?.decimals
                        )}{" "}
                        {fromCurrency?.symbol || ""}
                      </Typography.Text>
                    </Typography.Text>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: fromAmountError ? "1px solid #ff4d4f" : "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: 8,
                      padding: "0px 16px 0px 0px",
                      gap: 12,
                      height: 54,
                    }}
                  >
                    <Form.Item
                      name="fromCurrency"
                      style={{
                        margin: 0,
                        width: '150px',
                        minHeight: 54,
                      }}
                    >
                      <Select
                        value={fromCurrency?.id}
                        onChange={handleChangeFromCurrency}
                        dropdownStyle={{
                          background: "#1a1a1a",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          minHeight: 54,
                        }}
                        suffixIcon={
                          <ArrowIcon style={{ color: "white", fontSize: 12 }} />
                        }
                        className="select-custom-exchange"
                        popupClassName='custom-select-dropdown'
                      >
                        <OptGroup key='Fiat' label='Fiat'>
                          {dataAllCurrency.map((item: any) => {
                            if (item.type === 'FIAT')
                              return (
                                <Option key={item.id} value={item.id} className='currency-exchange-option'>
                                  <div className='currency-option'>
                                    <img
                                      src={
                                        `${process.env.REACT_APP_ROOT_URL}/${item.iconPath}`
                                      }
                                      onError={(e) => {
                                        e.currentTarget.src = smartLogoDefault;
                                      }}
                                      style={{
                                        objectFit: "cover",
                                        width: isTablet ? 24 : 32,
                                        height: isTablet ? 24 : 32,
                                        borderRadius: "50%",
                                      }}
                                      alt="currency"
                                    />
                                    <span>{item.symbol}</span>
                                  </div>
                                  {fromCurrency?.id === item.id ? <CheckIcon /> : <></>}
                                </Option>
                              )
                          })}
                        </OptGroup>
                        <OptGroup key='Crypto' label='Crypto'>
                          {dataAllCurrency.map((item: any) => {
                            if (item.type === 'CRYPTO')
                              return (
                                <Option key={item.id} value={item.id} className='currency-exchange-option'>
                                  <div className='currency-option'>
                                    <img
                                      src={
                                        `${process.env.REACT_APP_ROOT_URL}/${item.iconPath}`
                                      }
                                      onError={(e) => {
                                        e.currentTarget.src = smartLogoDefault;
                                      }}
                                      style={{
                                        objectFit: "cover",
                                        width: isTablet ? 24 : 32,
                                        height: isTablet ? 24 : 32,
                                        borderRadius: "50%",
                                      }}
                                      alt="currency"
                                    />
                                    <span>{item.symbol}</span>
                                  </div>
                                  {fromCurrency?.id === item.id && <CheckIcon />}
                                </Option>
                              )
                          })}
                        </OptGroup>
                      </Select>
                    </Form.Item>
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <Form.Item
                        name="fromAmount"
                        style={{ margin: 0, width: '100%' }}
                        validateTrigger="onBlur"
                        rules={[
                          // { required: true, message: '' },
                          {
                            validator: async (_, value) => {
                              if (!value) {
                                setFromAmountError(false);
                                return Promise.resolve();
                              }
                              const numericVal = Number(value);
                              if (numericVal <= 0) {
                                const errorMsg = 'The amount must be greater than 0';
                                setFromAmountError(true);
                                form.setFields([
                                  {
                                    name: 'fromAmount',
                                    errors: [
                                      errorMsg
                                    ],
                                  },
                                ]);
                                return Promise.reject(
                                  new Error(errorMsg)
                                );
                              }

                              // Max limit and min amount validation is done only in handleBlurFromAmount (on blur)
                              // Clear fromAmountError since basic validation passed
                              setFromAmountError(false);
                              return Promise.resolve();
                            },
                          },
                        ]}
                        help=""
                        validateStatus=""
                      >
                        <Input
                          onFocus={() => {
                            isFromAmountFocusedRef.current = true;
                          }}
                          onChange={(e) => {
                            // Mark that user has manually entered, no longer default
                            hasUserEditedInputRef.current = true;
                            // Reset currency change flag when user manually changes value
                            isChangingCurrencyRef.current = false;
                            // Clear preserved error when user manually enters a new value
                            preservedFromAmountErrorRef.current = null;
                            // Set direction to PAY_TO_RCV when user starts typing in fromAmount
                            setDirection(Direction.PAY_TO_RCV);
                            setLastChange('FROM');
                          }}
                          onBlur={() => {
                            isFromAmountFocusedRef.current = false;
                            handleBlurFromAmount();
                          }}
                          onPressEnter={() => {
                            isFromAmountFocusedRef.current = false;
                            handleBlurFromAmount();
                          }}
                          onKeyDown={(e) => {
                            const allowedKeys = [
                              "Backspace",
                              "Delete",
                              "ArrowLeft",
                              "ArrowRight",
                              "Tab",
                            ];
                            if (allowedKeys.includes(e.key)) return;

                            // Only allow numbers and decimal point
                            if (!/^[0-9.]$/.test(e.key)) {
                              e.preventDefault();
                              return;
                            }

                            const { value, selectionStart } = e.currentTarget;

                            // Only allow one decimal point
                            if (e.key === "." && value.includes(".")) {
                              e.preventDefault();
                              return;
                            }

                            // Check decimal places
                            if (value.includes(".")) {
                              const dotIndex = value.indexOf(".");
                              const decimals = value.substring(dotIndex + 1);

                              if (
                                selectionStart !== null &&
                                selectionStart > dotIndex &&
                                decimals.length >= fromDecimal
                              ) {
                                e.preventDefault();
                              }
                            }
                          }}
                          style={{
                            background: "transparent",
                            border: "none",
                            color: fromAmountError ? "#ff4d4f" : "white",
                            textAlign: "right",
                            fontSize: 16,
                            width: "100%",
                            paddingRight: 0,
                          }}
                          placeholder=""
                          className="input-custom-exchange"
                          disabled={totalBalance === 0 || loadingFee}
                        />
                      </Form.Item>
                      <div
                        style={{
                          borderLeft: "1px solid #FFFFFF59",
                          height: "16px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          className="exchange-modal-button-max"
                          type="text"
                          onClick={handleMaxFromAmount}
                          disabled={totalBalance === 0 || loadingFee}
                        >
                          Max
                        </Button>
                      </div>
                    </div>
                  </div>
                  {totalBalance === 0 ? (
                    <Typography.Text
                      style={{
                        fontSize: isTablet ? 10 : 12,
                        color: "#ff4d4f",
                        marginTop: 8,
                      }}
                    >
                      Your balance is below the minimum required to exchange. Please deposit more {fromCurrency?.symbol} to continue.
                    </Typography.Text>
                  ) : (() => {
                    const fromAmountErrors = form.getFieldError('fromAmount');
                    const hasError = fromAmountErrors && fromAmountErrors.length > 0;
                    const errorMessage = hasError ? fromAmountErrors[0] : null;
                    return errorMessage ? (
                      <Typography.Text
                        style={{
                          fontSize: isTablet ? 10 : 12,
                          color: "#ff4d4f",
                          marginTop: 8,
                        }}
                      >
                        {errorMessage}
                      </Typography.Text>
                    ) : null;
                  })()}
                </div>
                {/* To Section */}
                <div>
                  <Typography.Text
                    style={{
                      fontSize: isTablet ? 14 : 16,
                      fontWeight: 500,
                      color: "white",
                      marginBottom: 8,
                      display: "block",
                    }}
                  >
                    To
                  </Typography.Text>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: (toAmountError || toAmountMinError) ? "1px solid #ff4d4f" : "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: 8,
                      padding: "0px 16px 0px 0px",
                      gap: 12,
                      height: 54,
                    }}
                  >
                    <Form.Item
                      name="toCurrency"
                      style={{
                        margin: 0,
                        width: '150px',
                        minHeight: 54,
                      }}
                    >
                      <Select
                        value={toCurrency?.id}
                        onChange={handleChangeToCurrency}
                        dropdownStyle={{
                          background: "#1a1a1a",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          minHeight: 54,
                        }}
                        suffixIcon={
                          <ArrowIcon style={{ color: "white", fontSize: 12 }} />
                        }
                        className="select-custom-exchange"
                        popupClassName='custom-select-dropdown'
                      >
                        <OptGroup key='Fiat' label='Fiat'>
                          {dataAllCurrency.map((item: any) => {
                            if (item.type === 'FIAT')
                              return (
                                <Option key={item.id} value={item.id} className='currency-exchange-option'>
                                  <div className='currency-option'>
                                    <img
                                      src={
                                        `${process.env.REACT_APP_ROOT_URL}/${item.iconPath}`
                                      }
                                      onError={(e) => {
                                        e.currentTarget.src = smartLogoDefault;
                                      }}
                                      style={{
                                        objectFit: "cover",
                                        width: isTablet ? 24 : 32,
                                        height: isTablet ? 24 : 32,
                                        borderRadius: "50%",
                                      }}
                                      alt="currency"
                                    />
                                    <span>{item.symbol}</span>
                                  </div>
                                  {toCurrency?.id === item.id && <CheckIcon />}
                                </Option>
                              )
                          })}
                        </OptGroup>
                        <OptGroup key='Crypto' label='Crypto'>
                          {dataAllCurrency.map((item: any) => {
                            if (item.type === 'CRYPTO')
                              return (
                                <Option key={item.id} value={item.id} className='currency-exchange-option'>
                                  <div className='currency-option'>
                                    <img
                                      src={
                                        `${process.env.REACT_APP_ROOT_URL}/${item.iconPath}`
                                      }
                                      onError={(e) => {
                                        e.currentTarget.src = smartLogoDefault;
                                      }}
                                      style={{
                                        objectFit: "cover",
                                        width: isTablet ? 24 : 32,
                                        height: isTablet ? 24 : 32,
                                        borderRadius: "50%",
                                      }}
                                      alt="currency"
                                    />
                                    <span>{item.symbol}</span>
                                  </div>
                                  {toCurrency?.id === item.id && <CheckIcon />}
                                </Option>
                              )
                          })}
                        </OptGroup>
                      </Select>
                    </Form.Item>
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <Form.Item
                        name="toAmount"
                        style={{ margin: 0, width: '100%' }}
                        validateTrigger="onBlur"
                        rules={[
                          { required: true, message: '' },
                          {
                            validator: async (_, value) => {
                              if (!value) {
                                setToAmountMinError("");
                                return Promise.resolve()
                              }

                              // Only check fromAmount validation first if direction is PAY_TO_RCV (user is typing in fromAmount)
                              // If direction is RCV_TO_PAY (user is typing in toAmount), validate toAmount directly
                              if (direction === Direction.PAY_TO_RCV) {
                                // Check if fromAmount has errors first - only validate toAmount if fromAmount is valid
                                const fromAmountErrors = form.getFieldError('fromAmount');
                                if (fromAmountErrors && fromAmountErrors.length > 0) {
                                  // fromAmount has errors, don't validate toAmount
                                  setToAmountMinError("");
                                  setToAmountError(false);
                                  return Promise.resolve()
                                }

                                // Check if fromAmount is valid (not empty, > 0, within limits)
                                const fromAmountValue = form.getFieldValue('fromAmount');
                                if (!fromAmountValue || Number(fromAmountValue) <= 0) {
                                  // fromAmount is invalid, don't validate toAmount
                                  setToAmountMinError("");
                                  setToAmountError(false);
                                  return Promise.resolve()
                                }

                                // Check fromAmount min limit first
                                if (configFee && configFee && !loadingFee && !loadingFeeToCurrency) {
                                  const minFrom = getMinFromAmount();
                                  if (minFrom !== null && Number(fromAmountValue) < minFrom) {
                                    // fromAmount below min, don't validate toAmount
                                    setToAmountMinError("");
                                    setToAmountError(false);
                                    return Promise.resolve()
                                  }
                                }

                                // Then check fromAmount max limit
                                const maxLimitFrom = getMaxLimit(fromCurrency);
                                if (maxLimitFrom !== null && Number(fromAmountValue) > maxLimitFrom) {
                                  // fromAmount exceeds max, don't validate toAmount
                                  setToAmountMinError("");
                                  setToAmountError(false);
                                  return Promise.resolve()
                                }
                              }

                              // fromAmount is valid (or direction is RCV_TO_PAY), now validate toAmount
                              const numericVal = Number(value);
                              if (numericVal <= 0 || isNaN(numericVal) || !value.trim() || value === '') {
                                setToAmountMinError('The amount must be greater than 0');
                                return Promise.reject(
                                  new Error('The amount must be greater than 0')
                                )
                              }

                              // Min amount validation first using exchangeMoneyFee
                              // Skip min amount validation if configFee is not available or loading
                              // This prevents validation with old fee data when currency is changed
                              if (!configFee || loadingFeeToCurrency) {
                                // Use preserved error if:
                                // 1. We're actively changing currency, OR
                                // 2. configFee is null/loading and we have a preserved error (currency was just changed)
                                if (preservedToAmountErrorRef.current && (isChangingCurrencyRef.current || !configFee)) {
                                  setToAmountMinError(preservedToAmountErrorRef.current);
                                  setToAmountError(false);
                                  return Promise.reject(new Error(preservedToAmountErrorRef.current));
                                }
                                // If there's an existing error, keep it (don't clear)
                                const currentErrors = form.getFieldError('toAmount');
                                if (currentErrors.length > 0) {
                                  return Promise.reject(new Error(currentErrors[0]));
                                }
                                setToAmountMinError("");
                                setToAmountError(false);
                                // Continue to check max limit even if configFee is not ready
                              } else {
                                const minTo = getMinToAmount();
                                if (minTo !== null && numericVal < minTo) {
                                  const errorMsg = `The amount you receive must be at least ${formatNumber(minTo, toDecimal)} ${toCurrency?.symbol || ''}`;
                                  preservedToAmountErrorRef.current = errorMsg;
                                  setToAmountMinError(errorMsg);
                                  setToAmountError(false);
                                  return Promise.reject(new Error(errorMsg));
                                }
                              }

                              // Then check max limit for toCurrency - prioritize showing error in fromAmount
                              const maxLimit = getMaxLimit(toCurrency, true)

                              if (maxLimit !== null && numericVal > maxLimit) {
                                // Calculate backwards from toAmount to fromAmount
                                const toFrom = Number(numericVal / rate);
                                const conversionFee = calculateConversionFeeToCurrency(toFrom);
                                const fromAmountForMaxTo = (maxLimit / rate) + conversionFee;

                                // Prioritize checking max limit of fromAmount first
                                const maxLimitFrom = getMaxLimit(fromCurrency);
                                if (maxLimitFrom !== null && fromAmountForMaxTo > maxLimitFrom) {
                                  // fromAmount for max toAmount exceeds fromCurrency max limit
                                  // Show fromCurrency max limit error instead
                                  const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
                                  setFromAmountError(true);
                                  setToAmountError(false);
                                  setToAmountMinError("");
                                  form.setFields([
                                    {
                                      name: 'fromAmount',
                                      errors: [errorMsg]
                                    },
                                    { name: 'toAmount', errors: [] }
                                  ]);
                                  return Promise.reject(new Error(errorMsg));
                                }

                                // fromAmount for max toAmount is within fromCurrency limit
                                // Show error with calculated fromAmount
                                // const errorMsg = `Amount exceeds the maximum allowed limit. Maximum you can pay is ${formatNumber(fromAmountForMaxTo, fromDecimal)} ${fromCurrency?.symbol || ''}`;
                                const errorMsg = `Amount exceeds the maximum allowed limit of ${formatNumber(maxLimitFrom, fromDecimal)} ${fromCurrency?.symbol || ''}`;
                                setFromAmountError(true);
                                setToAmountError(false);
                                setToAmountMinError("");
                                form.setFields([
                                  {
                                    name: 'fromAmount',
                                    errors: [errorMsg]
                                  },
                                  { name: 'toAmount', errors: [] }
                                ]);
                                return Promise.reject(new Error(errorMsg));
                              }
                              // Validation passed - clear errors
                              preservedToAmountErrorRef.current = null;
                              setToAmountMinError("");
                              setToAmountError(false);
                              return Promise.resolve();
                            },
                          },
                        ]}
                        help=""
                        validateStatus=""
                      >
                        <Input
                          onFocus={() => {
                            isToAmountFocusedRef.current = true;
                          }}
                          onChange={(e) => {
                            // Mark that user has manually entered, no longer default
                            hasUserEditedInputRef.current = true;
                            // Reset currency change flag when user manually changes value
                            isChangingCurrencyRef.current = false;
                            // Clear preserved error when user manually enters a new value
                            preservedToAmountErrorRef.current = null;
                            // Set direction to RCV_TO_PAY when user starts typing in toAmount
                            setDirection(Direction.RCV_TO_PAY);
                            setLastChange('TO');
                          }}

                          style={{
                            background: "transparent",
                            border: "none",
                            color: "white",
                            textAlign: "right",
                            fontSize: 16,
                            width: "100%",
                          }}
                          onBlur={() => {
                            isToAmountFocusedRef.current = false;
                            handleBlurToAmount();
                          }}
                          onPressEnter={() => {
                            isToAmountFocusedRef.current = false;
                            handleBlurToAmount();
                          }}
                          onKeyDown={(e) => {
                            const allowedKeys = [
                              "Backspace",
                              "Delete",
                              "ArrowLeft",
                              "ArrowRight",
                              "Tab",
                            ];
                            if (allowedKeys.includes(e.key)) return;

                            // Only allow numbers and decimal point
                            if (!/^[0-9.]$/.test(e.key)) {
                              e.preventDefault();
                              return;
                            }

                            const { value, selectionStart } = e.currentTarget;

                            // Only allow one decimal point
                            if (e.key === "." && value.includes(".")) {
                              e.preventDefault();
                              return;
                            }

                            // Check decimal places
                            if (value.includes(".")) {
                              const dotIndex = value.indexOf(".");
                              const decimals = value.substring(dotIndex + 1);

                              if (
                                selectionStart !== null &&
                                selectionStart > dotIndex &&
                                decimals.length >= toDecimal
                              ) {
                                e.preventDefault();
                              }
                            }
                          }}
                          placeholder=""
                          suffix={isCalculating ? <Spin size="small" /> : null}
                          disabled={totalBalance === 0 || loadingFee || loadingFeeToCurrency}
                        />
                      </Form.Item>
                    </div>
                  </div>
                  <div>
                    {(() => {
                      const toAmountErrors = form.getFieldError('toAmount');
                      const hasError = (toAmountError || (toAmountErrors && toAmountErrors.length > 0)) && !toAmountMinError;
                      const errorMessage = toAmountErrors && toAmountErrors.length > 0
                        ? toAmountErrors[0]
                        : null;

                      // Show error if there's an error and no min error (min error is shown separately)
                      if (hasError && errorMessage) {
                        return (
                          <Typography.Text
                            style={{
                              fontSize: isTablet ? 10 : 12,
                              color: "#ff4d4f",
                              marginTop: 8,
                              display: "block",
                            }}
                          >
                            {errorMessage}
                          </Typography.Text>
                        );
                      }
                      return null;
                    })()}
                    {toAmountMinError && (
                      <Typography.Text
                        style={{
                          fontSize: isTablet ? 10 : 12,
                          color: "#ff4d4f",
                          marginTop: 8,
                          display: "block",
                        }}
                      >
                        {toAmountMinError}
                      </Typography.Text>
                    )}
                  </div>
                  <div>
                    <Typography.Text
                      style={{
                        fontSize: '12px',
                        fontWeight: 400,
                        color: 'rgba(242, 138, 40, 1)',
                        marginTop: 8,
                      }}
                      className="exchange-text-gradient"
                    >
                      {exchangeRateText}
                    </Typography.Text>
                  </div>
                </div>

                {/* Confirm Conversion Section */}
                <div
                  style={{
                    paddingTop: 24,
                    borderTop: "1px solid #FFFFFF26",
                  }}
                >
                  <div>
                    <Typography.Text
                      style={{
                        fontSize: isTablet ? 12 : 14,
                        fontWeight: 400,
                        color: 'white',
                        textAlign: 'center',
                        marginBottom: 8,
                      }}>
                      Confirm Conversion
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text
                      style={{
                        fontSize: isTablet ? 12 : 14,
                        fontWeight: 400,
                        color: 'white',
                        textAlign: 'center',
                      }}>
                      Fees:{' '}
                      <Typography.Text
                        style={{
                          fontSize: isTablet ? 12 : 14,
                          fontWeight: 400,
                          color: 'rgba(242, 138, 40, 1)',
                          textAlign: 'center',
                        }}>
                        {calculateConversionFee} {fromCurrency?.symbol || ""}
                      </Typography.Text>
                    </Typography.Text>
                  </div>
                </div>
              </div>
              <div
                style={{
                  padding: isTablet
                    ? "16px 16px 24px 16px"
                    : "24px 24px 32px 24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                }}
              >
                <CustomButton
                  type="submit"
                  size={isTablet ? "medium" : "large"}
                  text="Submit"
                  width={"100%"}
                  disabled={isSubmitDisabled}
                />
              </div>
            </Form>
          </Modal>

          <ConfirmModal
            type='warning'
            title='Please Confirm Changes'
            description='Fee or receive amount has been updated. Take a moment to review before continuing.'
            open={showReviewFee}
            onCancel={() => null}
            onSubmit={handleContinueReview}
            isLoading={false}
            textSubmit='OK'
            showButtonCancel={false}
          />
        </>
      ) : null}
    </>
  );
};

export default ModalExchange;
